import { useState } from 'react';
import toast from 'react-hot-toast';
import { useTranslation } from 'react-i18next';
import useHttp from '../../../../hooks/use-http';
import { urls } from './api/urls';
import {
  generisiIzvestajPDF,
  JEZIK_IZVESTAJA,
  napraviIzvestaj,
  TIP_IZVESTAJA,
} from './generisiIzvestajPDF';
import {
  generisiWorkReportPDF,
  napraviWorkReport,
  podrazumevaniMejlWorkReporta,
} from './generisiWorkReportPDF';
import { formatDateToDB } from '../../../../../utils/FormatDate';

export const OBJEKAT_WORK_REPORTA = 'WorkReportRN';

const kaoNiz = (rezultat) => (Array.isArray(rezultat) ? rezultat : []);
const generisiPDF = (opcije) =>
  opcije.izvestaj.workReport
    ? generisiWorkReportPDF(opcije)
    : generisiIzvestajPDF(opcije);
const zaglavljeUrl = (tip) =>
  tip === TIP_IZVESTAJA.PUR ? urls.GET_PUR_ZAGLAVLJE : urls.GET_RN_ZAGLAVLJE;

const useIzvestajRadnogNaloga = () => {
  const { getData } = useHttp('');
  const { t } = useTranslation();
  const [isLoading, setIsLoading] = useState(false);

  const post = (url, data) => getData({ method: 'POST', url, data });

  const ucitajZahtev = async (idZahteva, jezik) => {
    if (!idZahteva) return null;
    const rezultat = await post(urls.GET_SERVISNI_ZAHTEV_ZA_ID, {
      id: idZahteva,
      metadata: jezik,
    });
    return rezultat?.id > 0 ? rezultat : null;
  };

  // Cenovnik iz _VrsteUsluge po zaposlenom (tehničar/inženjer, domaći/ino kupac)
  const ucitajCeneUsluga = async (idDok, zaposleni, usluge, transport) => {
    const idZaposlenih = [
      ...zaposleni.map((red) => red.id),
      ...usluge.map((red) => red.idZaposlenog),
      ...transport.map((red) => red.idZaposlenog),
    ].filter((id, index, svi) => id > 0 && svi.indexOf(id) === index);
    const cene = await Promise.all(
      idZaposlenih.map((idZaposlenog) =>
        Promise.all([
          post(urls.GET_CENE_RADA_ZAPOSLENOG, { id: idDok, idZaposlenog }),
          post(urls.GET_CENE_PUTA_ZAPOSLENOG, { id: idDok, idZaposlenog }),
        ]),
      ),
    );
    return idZaposlenih.reduce(
      (acc, idZaposlenog, index) => ({
        ...acc,
        [idZaposlenog]: {
          rad: kaoNiz(cene[index][0]),
          put: kaoNiz(cene[index][1]),
        },
      }),
      {},
    );
  };

  // Zavisni pozivi (servisni zahtev, kupac) kreću čim stigne zaglavlje,
  // paralelno sa ostalim pozivima
  const ucitajPodatke = async (idDok, tip, jezik) => {
    const jePUR = tip === TIP_IZVESTAJA.PUR;
    const zaglavljePromise = post(zaglavljeUrl(tip), { id: idDok });
    const [
      zaglavlje,
      zahtev,
      [kupac, kontakti],
      zaposleni,
      usluge,
      transport,
      delovi,
      troskovi,
      opisIliStavke,
      dnevnikTeren,
      dnevnikBazaZnanja,
      beleske,
    ] = await Promise.all([
      zaglavljePromise,
      zaglavljePromise.then((z) =>
        jePUR ? null : ucitajZahtev(z?.idDokServisnogZahteva, jezik),
      ),
      zaglavljePromise.then((z) =>
        z?.idKupca
          ? Promise.all([
              post(urls.GET_KUPAC, { id: z.idKupca }),
              post(urls.GET_KONTAKTI_KUPCA, { id: z.idKupca }),
            ])
          : [null, []],
      ),
      post(urls.GET_ZAPOSLENI_ZA_RN, { id: idDok }),
      post(urls.GET_IZVRSENE_USLUGE, { id: idDok }),
      post(urls.GET_TRANSPORT, { id: idDok }),
      post(urls.GET_DELOVI, { id: idDok }),
      post(urls.GET_TROSKOVI, { id: idDok }),
      jePUR
        ? post(urls.GET_STAVKE_PUSTANJA, { id: idDok })
        : post(urls.GET_OPIS_RADOVA, { idDokRn: idDok }),
      post(urls.GET_DNEVNIK, { id: idDok, idTipaDnevnika: 1 }),
      post(urls.GET_DNEVNIK, { id: idDok, idTipaDnevnika: 2 }),
      jePUR
        ? post(urls.GET_BELESKE, { id: idDok, idTipaDnevnika: 1 })
        : Promise.resolve([]),
    ]);

    if (!zaglavlje?.idDok) {
      throw new Error(t('pdfDialog.greskaUcitavanja'));
    }

    const ceneUsluga = jePUR
      ? {}
      : await ucitajCeneUsluga(
          idDok,
          kaoNiz(zaposleni),
          kaoNiz(usluge),
          kaoNiz(transport),
        );

    return {
      primaoci: {
        ceoBrDok: zaglavlje.ceoBrDok,
        nazivKupca: zaglavlje.nazivKupca,
        kupac: kupac?.email ? kupac : null,
        kontakti: kaoNiz(kontakti).filter((kontakt) => kontakt.email),
      },
      podaci: {
        zaglavlje,
        zahtev,
        kupac,
        zaposleni: kaoNiz(zaposleni),
        usluge: kaoNiz(usluge),
        transport: kaoNiz(transport),
        ceneUsluga,
        delovi: kaoNiz(delovi),
        troskovi: kaoNiz(troskovi),
        opisRadova: jePUR ? [] : kaoNiz(opisIliStavke),
        stavke: jePUR ? kaoNiz(opisIliStavke) : [],
        dnevnikTeren: kaoNiz(dnevnikTeren),
        dnevnikBazaZnanja: kaoNiz(dnevnikBazaZnanja),
        beleske: kaoNiz(beleske),
      },
    };
  };

  // Servisni RN dobija work report za izabrani dan
  const napraviModel = (tip, podaci, datum) => {
    const dozvoljeneCene = localStorage.uniqueUser === 'true';
    return tip === TIP_IZVESTAJA.RN
      ? napraviWorkReport({ podaci, datum, dozvoljeneCene })
      : napraviIzvestaj({ tip, dozvoljeneCene, podaci });
  };

  const ucitajModel = async (idDok, tip, jezik, datum) =>
    napraviModel(tip, (await ucitajPodatke(idDok, tip, jezik)).podaci, datum);

  // pripremljen: model ili Promise modela (već učitani podaci); ako ga nema, podaci se učitavaju
  const pripremiIzvestaj = async (idDok, tip, jezik, pripremljen) => {
    const izvestaj =
      (await pripremljen) || (await ucitajModel(idDok, tip, jezik));
    const pdf = await generisiPDF({ izvestaj, jezik });
    return { ...pdf, izvestaj };
  };

  const sacuvajWorkReport = async (idDok, doc, nazivFajla, emailAdrese) => {
    const fd = new FormData();
    fd.append('IdVrsteDokOpste', 0);
    fd.append('objekat', OBJEKAT_WORK_REPORTA);
    fd.append('IdObjekta', idDok);
    fd.append('NazivFajla', nazivFajla);
    fd.append('Napomena', emailAdrese.join(', '));
    fd.append('IdJezika', 0);
    fd.append('Kategorija', null);
    fd.append('Datum', formatDateToDB(new Date()));
    fd.append('IdKorProm', +localStorage.idKorisnika);
    fd.append(
      'Dokumenti',
      new File([doc.output('blob')], nazivFajla, { type: 'application/pdf' }),
    );
    const rezultat = await post('Dokumenta/unos_dokumenta', fd);
    if (rezultat instanceof Error || rezultat?.isAxiosError) {
      throw new Error(t('pdfDialog.greskaCuvanja'));
    }
    // svi fajlovi naloga dele isti red u Dokumenta; novi fajl je onaj sa najvećim id (DokumentaURL.Id)
    const idjeviFajlova = kaoNiz(rezultat).map((f) => +f.id || 0);
    return idjeviFajlova.length ? Math.max(...idjeviFajlova) || null : null;
  };

  // evidencija slanja: jedan red po adresi, sa stvarnim ishodom slanja
  const upisiEvidencijuSlanja = async (podaci) => {
    const rezultat = await post(urls.UNOS_EMAIL_EVIDENCIJE, {
      ...podaci,
      idKorProm: +localStorage.idKorisnika,
    });
    if (rezultat?.rezultat !== 1) {
      throw new Error(t('pdfDialog.greskaEvidencije'));
    }
  };

  const izvrsi = async (akcija) => {
    if (isLoading) return false;
    setIsLoading(true);
    try {
      return await akcija();
    } catch (error) {
      console.error(error);
      toast.error(error?.message || t('pdfDialog.greskaKreiranja'));
      return false;
    } finally {
      setIsLoading(false);
    }
  };

  const ucitajIzvestaj = (idDok, tip, jezik, pripremljen = null) =>
    izvrsi(async () => (await pripremljen) || ucitajModel(idDok, tip, jezik));

  const pregledIzvestaja = (izvestaj, jezik) =>
    izvrsi(async () => {
      const { doc } = await generisiPDF({ izvestaj, jezik });
      return doc.output('bloburl');
    });

  const preuzmiIzvestaj = (
    idDok,
    tip,
    jezik = JEZIK_IZVESTAJA.SRB,
    izmenjenIzvestaj = null,
  ) =>
    izvrsi(async () => {
      if (!idDok) return false;
      const { doc, nazivFajla } = await pripremiIzvestaj(
        idDok,
        tip,
        jezik,
        izmenjenIzvestaj,
      );
      doc.save(nazivFajla);
      return true;
    });

  const posaljiIzvestaj = (
    idDok,
    tip,
    jezik,
    emailAdrese,
    izmenjenIzvestaj = null,
    mejl = null,
  ) =>
    izvrsi(async () => {
      if (!idDok || !emailAdrese.length) return false;
      const { doc, nazivFajla, izvestaj } = await pripremiIzvestaj(
        idDok,
        tip,
        jezik,
        izmenjenIzvestaj,
      );
      const base64Pdf = doc.output('datauristring').split(',')[1];
      const tekstMejla =
        mejl ||
        (izvestaj.workReport
          ? podrazumevaniMejlWorkReporta(jezik, izvestaj.broj, izvestaj.datum)
          : null);
      const rezultat = tekstMejla
        ? await post(urls.POSALJI_EMAIL_TEKST, {
            emailAdrese,
            base64Pdf,
            nazivFajla,
            naslov: tekstMejla.naslov,
            tekst: tekstMejla.tekst,
          })
        : await post(urls.POSALJI_EMAIL, {
            emailAdrese,
            base64Pdf,
            nazivFajla,
            ceoBrDok: izvestaj.zaglavlje.ceoBrDok,
            tip,
            jezik,
          });
      const uspesno = rezultat?.rezultat === 1;
      const greskaSlanja = rezultat?.opis || t('pdfDialog.greskaSlanja');
      if (izvestaj.workReport) {
        // bez liste neuspešnih (npr. greška servera) smatra se da nijedan mejl nije otišao
        const neuspesne = Array.isArray(rezultat?.neuspesneAdrese)
          ? rezultat.neuspesneAdrese.map((e) => e.trim().toLowerCase())
          : null;
        const adrese = emailAdrese.map((email) => {
          const poslato =
            neuspesne !== null &&
            !neuspesne.includes(email.trim().toLowerCase());
          return { email, poslato, greska: poslato ? null : greskaSlanja };
        });
        const poslate = adrese.filter((a) => a.poslato).map((a) => a.email);
        let idDokumentaURL = null;
        try {
          if (poslate.length) {
            idDokumentaURL = await sacuvajWorkReport(
              idDok,
              doc,
              nazivFajla,
              poslate,
            );
          }
        } catch (error) {
          console.error(error);
          toast.error(error.message);
        }
        try {
          await upisiEvidencijuSlanja({
            idDok,
            idDokumentaURL,
            nazivFajla,
            naslov: tekstMejla?.naslov || null,
            adrese,
          });
        } catch (error) {
          console.error(error);
          toast.error(error.message);
        }
      }
      if (!uspesno) {
        throw new Error(greskaSlanja);
      }
      toast.success(t('pdfDialog.poslato', { broj: emailAdrese.length }));
      return true;
    });

  return {
    preuzmiIzvestaj,
    posaljiIzvestaj,
    ucitajPodatke,
    ucitajZahtev,
    napraviModel,
    ucitajIzvestaj,
    pregledIzvestaja,
    isLoading,
  };
};

export { JEZIK_IZVESTAJA, TIP_IZVESTAJA };
export default useIzvestajRadnogNaloga;
